/**
 * Generic secondary "section rail" — thin quick-nav bar pinned above the app
 * footer with scroll-spy. Generalized from ScenarioSectionRail so any long
 * detail page (e.g. /cpt/{code}) can list its own sections.
 *
 * Items are declared in on-page scroll order; chips auto-filter to anchors
 * actually present (rescanning with backoff, since sections hydrate late on
 * direct loads), and the active chip follows the scroll position.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";

export interface PageRailItem {
  key: string;
  /** Anchor element id, or null to scroll to the top (Overview). */
  id: string | null;
  label: string;
  icon: React.ElementType;
}

export default function PageSectionRail({
  items,
  pageKey,
  dark = false,
  bottomOffset = "64px",
}: {
  items: PageRailItem[];
  /** Re-scan trigger — change when the underlying page content changes. */
  pageKey: string | number;
  dark?: boolean;
  bottomOffset?: string;
}) {
  const [present, setPresent] = useState<PageRailItem[]>(items.length ? [items[0]] : []);
  const [active, setActive] = useState<string>(items[0]?.key ?? "");
  const railRef = useRef<HTMLDivElement>(null);

  // Which sections actually rendered? Anchors mount asynchronously, so retry
  // with backoff rather than trusting a single early scan.
  useEffect(() => {
    let raf = 0;
    let timer: ReturnType<typeof setTimeout>;
    let tries = 0;
    const scan = () => {
      const found = items.filter((it) => it.id === null || document.getElementById(it.id));
      if (found.length > 1) {
        setPresent(found);
        return;
      }
      setPresent(items.length ? [items[0]] : []);
      if (++tries < 12) timer = setTimeout(() => { raf = requestAnimationFrame(scan); }, tries < 4 ? 350 : 1000);
    };
    timer = setTimeout(() => { raf = requestAnimationFrame(scan); }, 350);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [pageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll-spy: active = last section whose top passed the trigger line.
  useEffect(() => {
    let ticking = false;
    const compute = () => {
      ticking = false;
      const headerH = (document.querySelector("header") as HTMLElement)?.offsetHeight ?? 70;
      const threshold = headerH + 56;
      let current = items[0]?.key ?? "";
      for (const it of present) {
        if (!it.id) continue;
        const el = document.getElementById(it.id);
        if (el && el.getBoundingClientRect().top <= threshold) current = it.key;
      }
      setActive(current);
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(compute); } };
    window.addEventListener("scroll", onScroll, { passive: true });
    compute();
    return () => window.removeEventListener("scroll", onScroll);
  }, [present]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the active chip visible within the rail.
  useEffect(() => {
    const el = railRef.current?.querySelector<HTMLElement>(`[data-rail-key="${active}"]`);
    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [active]);

  const go = useCallback((it: PageRailItem) => {
    if (!it.id) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(it.id);
    if (!el) return;
    const headerH = (document.querySelector("header") as HTMLElement)?.offsetHeight ?? 70;
    const y = el.getBoundingClientRect().top + window.scrollY - headerH - 12;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  }, []);

  const barBg = dark ? "rgba(13,30,53,0.94)" : "rgba(255,255,255,0.94)";
  const border = dark ? "rgba(var(--civic-gold-rgb),0.22)" : "rgba(var(--civic-ink-rgb),0.10)";
  const idle = dark ? "rgba(var(--civic-warm-rgb),0.62)" : "rgba(var(--civic-ink-rgb),0.62)";
  const accent = dark ? "var(--civic-gold)" : "var(--civic-red)";
  const activeBg = dark ? "rgba(var(--civic-gold-rgb),0.16)" : "rgba(var(--civic-red-rgb),0.08)";

  if (present.length <= 1) return null;

  return (
    <div
      ref={railRef}
      data-rail-bar
      className="fixed left-0 right-0 flex items-stretch gap-1.5 px-2.5 overflow-x-auto no-scrollbar"
      style={{
        bottom: bottomOffset,
        zIndex: 9990,
        height: 46,
        backgroundColor: barBg,
        borderTop: `1px solid ${border}`,
        backdropFilter: "saturate(180%) blur(16px)",
        WebkitBackdropFilter: "saturate(180%) blur(16px)",
        scrollbarWidth: "none",
      }}
    >
      {present.map((it) => {
        const Icon = it.icon;
        const on = active === it.key;
        return (
          <button
            key={it.key}
            data-rail-key={it.key}
            onClick={() => go(it)}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 my-1.5 rounded-full transition-colors"
            style={{
              color: on ? accent : idle,
              backgroundColor: on ? activeBg : "transparent",
              border: `1px solid ${on ? `${accent}55` : "transparent"}`,
              fontFamily: "'Instrument Sans', system-ui, sans-serif",
              fontSize: 12,
              fontWeight: on ? 700 : 500,
              whiteSpace: "nowrap",
            }}
          >
            <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={on ? 2.4 : 2} />
            {it.label}
          </button>
        );
      })}
    </div>
  );
}
